<!-- **** --------------------------------------------------------------------------
	SHARED SCRIPT
-------------------------------------------------------------------------- **** -->
<script lang="ts">
    import { computed, ComputedRef, onMounted, ref } from 'vue';

    // utils
    import { uid } from '@utils';

    // assets
    import { SmallArrowDownSVG, SmallArrowUpSVG, CrossSVG } from '@shared/assets/vector/ui/IconsUI';

    // this is mostly for debugging, otherwise components are Anonymous
    export default {
        name: 'Select Input: CSSModule',
    };
</script>

<!-- **** --------------------------------------------------------------------------
	SCRIPT
-------------------------------------------------------------------------- **** -->
<script lang="ts" setup>
    // components
    import ScrollableContainer from './ScrollableContainer.vue';
    import { EventEmitter } from 'stream';

    const props = withDefaults(
        defineProps<{
            items: any;
            value: any;
            selectionProp?: string | number;
            displayProp?: string | number;
            itemHeight?: number;
            placeholder?: string;
            selectMany?: boolean;
            onChange?: (val: any) => void;
        }>(),
        {
            selectMany: false,
            itemHeight: 24,
        }
    );

    let inputValue = ref('');

    const options = {
        maxSize: 350,
        sizeThreshold: 200,
        containerMargin: 40,
        direction: 'drop-down',
        staticValues: true,
        spacing: 2,
    };

    /** component key: used to identify the specific instance of component */
    const cKey = uid();

    const handleOutsideClick = (ev: MouseEvent) => {
        if (!active.value) {
            document.removeEventListener('click', handleOutsideClick);
            return;
        }

        for (let elem of ev.composedPath()) {
            let el = elem as HTMLElement;
            if (el.dataset?.key === cKey) return;
        }

        active.value = false;
        document.removeEventListener('click', handleOutsideClick);
    };

    const selected = ref(props.value);
    const handleSelect = (ev: MouseEvent, item: any, emit: (event: string, ...args: any[]) => void) => {
        if (props.selectMany) selected.value.push(props.selectionProp ? item[props.selectionProp] : item);
        else selected.value = props.selectionProp ? item[props.selectionProp] : item;

        props.onChange && props.onChange(selected.value);
        emit('update:value', selected.value);
        active.value = false;
        document.removeEventListener('click', handleOutsideClick);
    };

    const removeFromSelection = (index: number, emit: (event: string, ...args: any[]) => void) => {
        selected.value.splice(index, 1);
        props.onChange && props.onChange(selected.value);
        emit('update:value', selected.value);
    };

    const formattedList: ComputedRef<any> = computed(() => {
            return Object.values(props.items).reduce(
                (acc: any, item: any, idx: number) => (props.selectionProp ? (acc[item[props.selectionProp]] = item) : (acc[idx] = item), acc),
                {}
            );
        }),
        filteredItems: ComputedRef<any> = computed(() => {
            return Object.values(props.items);
        }),
        active = ref(false),
        direction = ref(options.direction),
        maxSize = ref(options.maxSize),
        activeSize = computed<number>(() => {
            let nodeArea = filteredItems.value.length * props.itemHeight + options.spacing;
            return nodeArea > options.maxSize ? options.maxSize : nodeArea;
        });

    const handleActive = (ev: MouseEvent | FocusEvent, show?: boolean) => {
        if (active.value === show) return;
        active.value = typeof show === 'undefined' ? !active.value : show;

        if (active.value) {
            let el = ev.target as HTMLElement,
                rect = el.getBoundingClientRect(),
                distance = rect.y + options.maxSize + options.containerMargin;

            if (options.direction === 'drop-down' && distance > window.innerHeight) {
                let difference = distance - window.innerHeight;

                if (difference < options.maxSize - options.sizeThreshold) {
                    maxSize.value = options.maxSize - difference;
                    direction.value = 'drop-down';
                } else {
                    maxSize.value = options.maxSize;
                    direction.value = 'drop-up';
                }
            } else {
                direction.value = options.direction;
                maxSize.value = options.maxSize;
            }

            document.addEventListener('click', handleOutsideClick);
        }
    };
</script>

<!-- **** --------------------------------------------------------------------------
	TEMPLATE
-------------------------------------------------------------------------- **** -->
<template>
    <div class="select-wrapper" :data-key="cKey" :class="[direction, active && 'active']" :style="`--item-height: ${itemHeight}px`">
        <div class="dropdown-list" :class="[direction, active && 'active']" :style="{ height: active ? `${activeSize}px` : 0 }">
            <ScrollableContainer :class="'xs-white-space grey standard-overflow'">
                <div
                    class="list-item"
                    v-for="item of filteredItems"
                    :key="`${cKey}item${selectionProp ? item[selectionProp] : item}`"
                    @click="(ev) => handleSelect(ev, item, $emit)">
                    <h4>{{ displayProp ? item[displayProp] : item }}</h4>
                </div>
            </ScrollableContainer>
        </div>

        <div class="select-container">
            <template v-if="selectMany">
                <div class="badge" v-for="(item, index) in selected" :key="item">
                    <h4>{{ displayProp ? formattedList[item][displayProp] : item }}</h4>
                    <div class="remove-button" @click="() => removeFromSelection(index, $emit)">
                        <CrossSVG />
                    </div>
                </div>
            </template>

            <div
                class="scalable-input"
                :class="selectMany && 'pseudo-hidden'"
                :data-value="selectMany ? inputValue : items[value] ? displayProp && formattedList[value][displayProp] : ''">
                <input
                    type="text"
                    @focus="(ev) => handleActive(ev, true)"
                    v-model="inputValue"
                    :placeholder="value?.length === 0 ? placeholder : ''"
                    :readonly="options.staticValues" />
            </div>

            <div class="input-helper-button right" @click="(ev) => handleActive(ev)">
                <SmallArrowDownSVG v-if="!active" />
                <SmallArrowUpSVG v-if="active" />
            </div>
        </div>
    </div>
</template>

<!-- **** --------------------------------------------------------------------------
	STYLES
-------------------------------------------------------------------------- **** -->
<style>
    .select-wrapper {
        --input-height: 2.4rem;
        --item-height: 35px;
        position: relative;
        width: 100%;
        height: 2.4;
        user-select: none;
    }

    .select-wrapper.active {
        z-index: 10;
    }

    .select-container {
        display: flex;
        position: relative;
        flex-wrap: wrap;
        min-height: 2.4rem;
        width: 100%;
        padding: 0 calc(var(--input-height) + 0.3rem) 0 0.1rem;
        border: 0.05rem solid #97cdc6;

        border-radius: 0.2rem;
        background-color: #fff;
    }
    .scalable-input {
        display: block;
        position: relative;
        flex-shrink: 1;
        flex-grow: 1;
        flex-basis: 20%;
    }
    .scalable-input input {
        font-size: 0.85rem;
        color: #2d3729;
        position: absolute;
        top: 0;
        left: 0;
        height: 2.2rem;
        width: 100%;
        min-width: 0;
        padding: 0.15rem 0.3rem 0;
        outline: none;
        border: none;
        background: none;
    }

    .scalable-input::before {
        content: attr(data-value);
        color: #2d3729;
        display: block;
        position: relative;
        padding: 0.2rem 0.3rem;
        margin: 0.15rem 0 0rem 0;
        font-size: 0.7rem;
        pointer-events: none;
        user-select: none;
    }
    .scalable-input.pseudo-hidden::before {
        visibility: hidden;
    }

    .badge {
        display: flex;
        align-items: center;
        padding: 0.3rem 0.1rem 0.1rem 0.5rem;
        margin: 0.1rem 0 0.1rem 0.15rem;
        border: 1px solid var(--clr-jellyfish);
        border-radius: 0.15rem;
    }

    .badge h4 {
        font-size: 0.66rem;
        line-height: 0.66rem;
    }

    .remove-button {
        display: flex;
        justify-content: center;
        align-items: center;
        height: 1rem;
        width: 1rem;
        margin-left: 0.05rem;
        opacity: 0.5;
    }
    .remove-button:hover {
        opacity: 1;
        cursor: pointer;
    }
    .remove-button svg {
        height: 0.6rem;
        width: 0.6rem;
    }

    .scalable-input input:read-only {
        cursor: unset;
    }

    .active.drop-up .select-container,
    .drop-down .dropdown-list {
        border-radius: 0 0 0.2rem 0.2rem;
    }
    .active.drop-down .select-container,
    .drop-up .dropdown-list {
        border-radius: 0.2rem 0.2rem 0 0;
    }

    .dropdown-list {
        display: flex;
        flex-direction: column;
        position: absolute;

        height: 0;
        width: 100%;
        top: 0.8rem;
        padding-top: 0;
        padding-bottom: 0;
        border: 1px solid var(--clr-jellyfish);

        border-radius: 0.2rem;
        background-color: #fff;
        user-select: none;

        transition: height 0.2s ease;
    }

    .dropdown-list .scrollable-wrapper {
        overflow: hidden;
    }

    .select-wrapper.active .scrollable-wrapper {
        overflow-y: auto;
    }

    .drop-down .dropdown-list {
        top: calc(100% - 0.2rem);
        /* padding-top: 0.13rem; */
        box-shadow: 0rem 0.6rem 0.3rem -0.4rem #e1c4c4b5;
    }
    .select-wrapper.active.drop-down .dropdown-list {
        bottom: unset;
    }
    .drop-up .dropdown-list {
        bottom: calc(100% - 0.2rem);
        /* padding-bottom: 0.13rem; */
    }
    .select-wrapper.drop-up .dropdown-list {
        top: unset;
    }

    .select-wrapper.fixed-height .drop-down {
        top: var(--input-height);
    }
    .select-wrapper.fixed-height .drop-up {
        bottom: var(--input-height);
    }

    .list-item {
        display: flex;
        align-items: center;
        flex-shrink: 0;
        flex-grow: 0;
        position: relative;
        height: var(--item-height);
        width: 100%;
        max-width: 100%;
        padding: 0 0.3rem;
        overflow-x: hidden;
    }

    .list-item h4 {
        color: #2d3729;
        font-size: 0.85rem;
    }

    .list-item:hover {
        background-color: #721a1a0d;
    }
</style>
